import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useRef } from 'react';
import { Drawer, DrawerContent } from '@music-vine/cadence/ui';
import { ScrollArea } from '@music-vine/cadence/ui';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { GlobalSearchForm } from './globalSearchForm';
import { GlobalSearchPopup } from './globalSearchPopup';
import type { SearchResults } from './searchStorage';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { CSSProperties } from 'react';
interface CustomCSSProperties extends CSSProperties {
  '--fade-away-from'?: string;
  '--fade-away-from-dark'?: string;
  '--fade-away-to'?: string;
  '--fade-away-to-dark'?: string;
}
interface SearchPopupProps {
  activeAssetType: AssetType;
  closeSearch: () => void;
  getPlaceholderText: string;
  handleAssetTypeChange: (value: AssetType) => void;
  handleInputBlur: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
  isHovered: boolean;
  isSearchVisible: boolean;
  isSelectOpened: boolean;
  openSearch: () => void;
  searchResults: SearchResults | undefined;
  setIsHovered: (value: boolean) => void;
  setIsSelectOpened: (value: boolean) => void;
  setSearchResults: React.Dispatch<React.SetStateAction<SearchResults | undefined>>;
}
export const SearchPopup = ({
  isSearchVisible,
  closeSearch,
  searchResults,
  activeAssetType,
  isHovered,
  isSelectOpened,
  setIsHovered,
  setIsSelectOpened,
  openSearch,
  handleInputChange,
  handleKeyDown,
  handleInputBlur,
  handleSubmit,
  handleAssetTypeChange,
  getPlaceholderText,
  setSearchResults
}: SearchPopupProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const {
    isMd
  } = useTWScreens();
  const scrollAreaStyles = useMemo(() => ({
    '--fade-away-from': 'rgb(243 244 246)',
    '--fade-away-to': 'rgb(243 244 246 / 0)',
    '--fade-away-from-dark': 'rgb(34 38 38)',
    '--fade-away-to-dark': 'rgb(34 38 38 / 0)'
  }) as CustomCSSProperties, []);
  const mobileScrollAreaStyles = useMemo(() => ({
    '--fade-away-from': 'rgb(255 255 255)',
    '--fade-away-to': 'rgb(255 255 255 / 0)',
    '--fade-away-from-dark': 'rgb(21 26 32)',
    '--fade-away-to-dark': 'rgb(21 26 32 / 0)'
  }) as CustomCSSProperties, []);
  const SearchPopupContent = useMemo(() => <GlobalSearchPopup activeAssetType={activeAssetType} closePopup={closeSearch} key={`search-content-${activeAssetType}`} results={searchResults?.results} searchTerm={searchResults?.searchTerm} />, [searchResults?.searchTerm, searchResults?.results, closeSearch, activeAssetType]);

  // Show as much as possible when opening the search popup
  const maxHeightClass = searchResults?.searchTerm ? 'max-h-[90vh]' : 'max-h-[95vh]';

  // Mobile drawer version
  if (!isMd) {
    return (
      // `repositionInputs={false}`: vaul's built-in keyboard handling
      // over-compensates for this partial-height drawer, clipping results
      // behind the iOS keyboard.
      <Drawer onOpenChange={closeSearch} open={isSearchVisible} repositionInputs={false}>
        <DrawerContent className="h-[75svh] px-0 pt-7 pb-0 outline-hidden ring-0 focus:outline-hidden focus:ring-0 focus-visible:outline-hidden focus-visible:ring-0 dark:bg-gray-875!">
          <div className="flex h-full flex-col">
            {/* Search Form in Drawer */}
            <div className="md:p-4">
              <GlobalSearchForm getPlaceholderText={getPlaceholderText} handleAssetTypeChange={handleAssetTypeChange} handleInputBlur={handleInputBlur} handleInputChange={handleInputChange} handleKeyDown={handleKeyDown} handleSubmit={handleSubmit} isHovered={isHovered} isSearchVisible={isSearchVisible} isSelectOpened={isSelectOpened} openSearch={openSearch} setIsHovered={setIsHovered} setIsSelectOpened={setIsSelectOpened} setSearchResults={setSearchResults} />
            </div>

            {/* Search Results */}
            <div className="flex-1 overflow-hidden" ref={contentRef}>
              <ScrollArea className="[&_[data-radix-scroll-area-viewport]>div]:block! h-full" hideScrollBar style={mobileScrollAreaStyles}>
                <AnimatePresence mode="wait">
                  <motion.div animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} initial={{
                    opacity: 0
                  }} key={`content-${activeAssetType}`} transition={{
                    opacity: {
                      duration: 0.15,
                      ease: [0.33, 1, 0.68, 1]
                    }
                  }}>
                    {SearchPopupContent}
                  </motion.div>
                </AnimatePresence>
              </ScrollArea>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop popup version
  return <AnimatePresence mode="sync" data-sentry-element="AnimatePresence" data-sentry-component="SearchPopup" data-sentry-source-file="searchPopup.tsx">
      {isSearchVisible && <motion.div animate={{
      opacity: 1,
      y: 0,
      height: 'auto'
    }} className="absolute top-full left-0 z-50 mt-[-1px] w-full overflow-hidden sm:max-w-[380px] lg:max-w-[450px] xl:max-w-[530px] 2xl:max-w-[572px]" exit={{
      opacity: 0,
      y: -5,
      height: 0
    }} initial={{
      opacity: 0,
      y: -5,
      height: 0
    }} style={{
      transform: 'translateZ(0)',
      backfaceVisibility: 'hidden',
      perspective: 1000,
      willChange: 'transform, opacity, height'
    }} transition={{
      duration: 0.15,
      ease: [0.33, 1, 0.68, 1]
    }}>
          <ScrollArea className={`rounded-b-[20px] bg-white [box-shadow:0px_15px_20px_0px_rgba(0,0,0,0.08)] dark:bg-gray-875 ${maxHeightClass} [&_[data-radix-scroll-area-viewport]>div]:block! flex h-max flex-col overflow-y-auto`} fadeAway="bottom" hideScrollBar style={scrollAreaStyles}>
            <div className="p-5 pt-2.5" ref={contentRef}>
              <AnimatePresence mode="wait">
                <motion.div animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} initial={{
              opacity: 0
            }} key={`content-${activeAssetType}`} transition={{
              opacity: {
                duration: 0.15,
                ease: [0.33, 1, 0.68, 1]
              }
            }}>
                  {SearchPopupContent}
                </motion.div>
              </AnimatePresence>
            </div>
          </ScrollArea>
        </motion.div>}
    </AnimatePresence>;
};